import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { CommonTable } from "../../../shared/ui/table/CommonTable";
import { db, functions } from "../../../firebase";
import { SeecPageHeader } from "./SeecPageHeader";
import { isPacDonorProfile } from "../utils/seecEntityUtils";

function formatMoney(value) {
  const amount = Number(value || 0);
  return `$${amount.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US");
}

function buildAddressLabel(address) {
  if (!address) return "";

  return [
    address.streetAddress,
    address.townName || address.city,
    address.state,
    address.zipCode,
  ]
    .filter(Boolean)
    .join(", ");
}

function buildMapUrl(addressLabel) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey || !addressLabel) return null;

  return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(
    addressLabel,
  )}`;
}

function formatPhone(value) {
  const raw = String(value || "").trim();
  return raw || "—";
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
    </div>
  );
}

export function SeecDonorCard() {
  const { docId } = useParams();
  const [donor, setDonor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pacEnrichmentState, setPacEnrichmentState] = useState("idle");
  const [pacEnrichmentError, setPacEnrichmentError] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadDonor() {
      setLoading(true);
      setLoadError("");

      try {
        const snap = await getDoc(doc(db, "SEEC_DONORS", docId));

        if (!isMounted) return;

        if (!snap.exists()) {
          setDonor(null);
          setLoadError("Donor not found.");
          return;
        }

        setDonor({ id: snap.id, ...snap.data() });
      } catch (error) {
        if (!isMounted) return;
        setLoadError(error.message || "Unable to load donor.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadDonor();

    return () => {
      isMounted = false;
    };
  }, [docId]);

  const primaryAddressLabel = useMemo(
    () => buildAddressLabel(donor?.primaryAddress),
    [donor],
  );
  const isPac = useMemo(() => isPacDonorProfile(donor), [donor]);
  const pacProfile = donor?.pacProfile || {};
  const pacAddressLabel = useMemo(
    () => buildAddressLabel(pacProfile.mailingAddress) || primaryAddressLabel,
    [pacProfile.mailingAddress, primaryAddressLabel],
  );
  const mapAddressLabel = isPac ? pacAddressLabel : primaryAddressLabel;
  const mapUrl = useMemo(() => buildMapUrl(mapAddressLabel), [mapAddressLabel]);
  const overviewRows = useMemo(() => {
    const sharedRows = [
      {
        id: "displayName",
        label: "Display Name",
        value: donor?.displayName || "—",
      },
      {
        id: "primaryAddress",
        label: isPac ? "Mailing Address" : "Primary Address",
        value: pacAddressLabel || "—",
      },
    ];

    if (isPac) {
      return [
        ...sharedRows,
        {
          id: "committeeType",
          label: "Committee Type",
          value: pacProfile.committeeType || "PAC",
        },
        {
          id: "website",
          label: "Website",
          value: pacProfile.website ? (
            <a
              href={pacProfile.website}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 hover:text-blue-900 underline"
            >
              {pacProfile.website}
            </a>
          ) : (
            "—"
          ),
        },
        {
          id: "contactEmail",
          label: "Contact Email",
          value: pacProfile.contactEmail || "—",
        },
        {
          id: "contactPhone",
          label: "Contact Phone",
          value: formatPhone(pacProfile.contactPhone),
        },
        {
          id: "contactName",
          label: "Contact Name",
          value: pacProfile.contactName || "—",
        },
        {
          id: "treasurerName",
          label: "Treasurer",
          value: pacProfile.treasurerName || "—",
        },
      ];
    }

    return [
      ...sharedRows,
      {
        id: "primaryOccupation",
        label: "Primary Occupation",
        value: donor?.primaryOccupation || "—",
      },
      {
        id: "primaryEmployer",
        label: "Primary Employer",
        value: donor?.primaryEmployer || "—",
      },
    ];
  }, [donor, isPac, pacAddressLabel, pacProfile]);

  const summary = donor?.donationSummary || {};
  const yearlyTotals = donor?.yearlyTotals || [];
  const knownNames = donor?.knownNames || [];
  const addresses = donor?.addresses || [];
  const townTotals = donor?.townTotals || [];
  const organizationTotals = donor?.organizationTotals || [];
  const donations = donor?.donations || [];

  const runPacEnrichment = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!donor) return;

    setPacEnrichmentState("loading");
    setPacEnrichmentError("");

    try {
      const callable = httpsCallable(functions, "enrichSeecPacProfile");
      const result = await Promise.race([
        callable({ docId: donor.id, forceRefresh }),
        new Promise((_, reject) => {
          window.setTimeout(() => {
            reject(new Error("PAC enrichment timed out after 30 seconds."));
          }, 30000);
        }),
      ]);
      const nextDonor = result?.data?.donor;

      if (nextDonor) {
        setDonor(nextDonor);
      }

      setPacEnrichmentState("done");
    } catch (error) {
      setPacEnrichmentState("error");
      setPacEnrichmentError(error.message || "Unable to enrich PAC details.");
      console.error("PAC enrichment failed:", error);
    }
  }, [donor, functions]);

  useEffect(() => {
    if (!donor || !isPac) return undefined;
    if (pacProfile?.lastEnrichedAt) return undefined;
    if (pacProfile?.enrichmentStatus === "failed") {
      setPacEnrichmentState("error");
      setPacEnrichmentError(
        pacProfile?.lastEnrichmentError || "PAC enrichment previously failed.",
      );
      return undefined;
    }
    if (pacEnrichmentState === "loading" || pacEnrichmentState === "done") return undefined;

    let isCancelled = false;

    async function enrichPacProfile() {
      try {
        await runPacEnrichment();

        if (isCancelled) return;
      } catch (error) {
        if (isCancelled) return;
      }
    }

    enrichPacProfile();

    return () => {
      isCancelled = true;
    };
  }, [
    donor,
    functions,
    isPac,
    pacEnrichmentState,
    pacProfile?.enrichmentStatus,
    pacProfile?.lastEnrichedAt,
    pacProfile?.lastEnrichmentError,
    runPacEnrichment,
  ]);

  return (
    <div className="space-y-5 text-gray-800">
      <div>
        <div className="flex flex-col gap-5">
          <SeecPageHeader title={donor?.displayName || "Donor profile"} active="donors" description="Contribution history, campaign totals, and donor details." />
          <Link to="/seec/top-donors" className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900">
            <ArrowLeftIcon className="w-4 h-4" />Back to top donors
          </Link>

          {loading ? (
            <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-md">
              Loading donor...
            </div>
          ) : loadError ? (
            <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-md">
              <div className="text-red-600">{loadError}</div>
              <div className="mt-3">
                <Link to="/seec" className="text-blue-700 hover:text-blue-900 underline">
                  Return to SEEC reports
                </Link>
              </div>
            </div>
          ) : donor ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard
                  label="Total Donated"
                  value={formatMoney(summary.totalAmount)}
                />
                <StatCard
                  label="Donation Count"
                  value={(summary.donationCount || 0).toLocaleString()}
                />
                <StatCard
                  label="First Donation"
                  value={formatDate(summary.firstDonationDate)}
                />
                <StatCard
                  label="Last Donation"
                  value={formatDate(summary.lastDonationDate)}
                />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] gap-5">
                <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                  <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                    Donor Overview
                  </div>
                  <div className="bg-white p-4">
                    {isPac && pacEnrichmentState === "loading" ? (
                      <div className="mb-3 text-sm text-gray-600">
                        Loading PAC contact information...
                      </div>
                    ) : null}
                    {isPac && pacEnrichmentState === "error" ? (
                      <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm text-amber-700">
                          {pacEnrichmentError || "PAC contact enrichment failed."}
                        </div>
                        <button
                          type="button"
                          onClick={() => runPacEnrichment({ forceRefresh: true })}
                          className="px-3 py-1.5 rounded border border-blue-300 bg-blue-50 text-sm text-blue-700 hover:bg-blue-100"
                        >
                          Retry PAC Enrichment
                        </button>
                      </div>
                    ) : null}
                    <CommonTable
                      title=""
                      showAggregateTotal={false}
                      hideColumnHeaders={true}
                      headerColor="bg-blue-50"
                      columns={[
                        {
                          key: "label",
                          label: "Field",
                          style: { width: "220px", fontWeight: 600 },
                        },
                        { key: "value", label: "Value" },
                      ]}
                      data={[
                        ...overviewRows,
                        {
                          id: "towns",
                          label: "Towns Donated To",
                          value: (summary.townsDonatedTo || []).join(", ") || "—",
                        },
                        {
                          id: "organizations",
                          label: "Organizations Donated To",
                          value: Array.isArray(summary.organizationsDonatedTo) &&
                            summary.organizationsDonatedTo.length > 0 ? (
                            <div className="flex flex-col">
                              {summary.organizationsDonatedTo.map((name) => (
                                <span key={name}>{name}</span>
                              ))}
                            </div>
                          ) : (
                            "—"
                          ),
                        },
                      ]}
                    />
                  </div>
                </div>

                <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                  <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                    Google Maps
                  </div>
                  <div className="bg-white p-4">
                    {mapUrl ? (
                      <iframe
                        title="Donor location"
                        src={mapUrl}
                        width="100%"
                        height="320"
                        style={{ border: 0, borderRadius: "0.5rem" }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    ) : (
                      <div className="text-sm text-gray-500">
                        No usable address is available for this donor yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                  Yearly Totals
                </div>
                <div className="bg-white p-4">
                  <CommonTable
                    title=""
                    showAggregateTotal={false}
                    headerColor="bg-blue-50"
                    defaultSortField="year"
                    defaultSortOrder="desc"
                    columns={[
                      {
                        key: "year",
                        label: "Year",
                        style: { width: "110px", textAlign: "center" },
                        align: "center",
                        headerAlign: "center",
                      },
                      {
                        key: "amount",
                        label: "Total Donated",
                        style: { width: "160px" },
                        align: "right",
                        render: (row) => formatMoney(row.amount),
                      },
                      {
                        key: "donationCount",
                        label: "# of Donations",
                        style: { width: "150px" },
                        align: "center",
                      },
                    ]}
                    data={yearlyTotals}
                    emptyMessage="No yearly totals are available for this donor."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                  <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                    Known Names
                  </div>
                  <div className="bg-white p-4">
                    <CommonTable
                      title=""
                      showAggregateTotal={false}
                      headerColor="bg-blue-50"
                      defaultSortField="lastSeenDate"
                      defaultSortOrder="desc"
                      columns={[
                        {
                          key: "displayName",
                          label: "Name",
                          style: { width: "220px" },
                        },
                        {
                          key: "count",
                          label: "Count",
                          style: { width: "90px" },
                          align: "center",
                        },
                        {
                          key: "firstSeenDate",
                          label: "First Seen",
                          style: { width: "120px" },
                          render: (row) => formatDate(row.firstSeenDate),
                        },
                        {
                          key: "lastSeenDate",
                          label: "Last Seen",
                          style: { width: "120px" },
                          render: (row) => formatDate(row.lastSeenDate),
                        },
                      ]}
                      data={knownNames}
                      emptyMessage="No alternate donor names were recorded."
                    />
                  </div>
                </div>

                <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                  <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                    Addresses
                  </div>
                  <div className="bg-white p-4">
                    <CommonTable
                      title=""
                      showAggregateTotal={false}
                      headerColor="bg-blue-50"
                      defaultSortField="lastSeenDate"
                      defaultSortOrder="desc"
                      columns={[
                        {
                          key: "addressLabel",
                          label: "Address",
                          style: { width: "320px" },
                          render: (row) => row.addressLabel || buildAddressLabel(row) || "—",
                        },
                        {
                          key: "donationCount",
                          label: "# of Donations",
                          style: { width: "130px" },
                          align: "center",
                        },
                        {
                          key: "totalAmount",
                          label: "Total Donated",
                          style: { width: "130px" },
                          align: "right",
                          render: (row) => formatMoney(row.totalAmount),
                        },
                        {
                          key: "lastSeenDate",
                          label: "Last Seen",
                          style: { width: "120px" },
                          render: (row) => formatDate(row.lastSeenDate),
                        },
                      ]}
                      data={addresses}
                      emptyMessage="No donor addresses were recorded."
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                  <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                    Town Totals
                  </div>
                  <div className="bg-white p-4">
                    <CommonTable
                      title=""
                      showAggregateTotal={false}
                      headerColor="bg-blue-50"
                      defaultSortField="amount"
                      defaultSortOrder="desc"
                      columns={[
                        { key: "townName", label: "Town", style: { width: "220px" } },
                        {
                          key: "amount",
                          label: "Total Donated",
                          style: { width: "130px" },
                          align: "right",
                          render: (row) => formatMoney(row.amount),
                        },
                        {
                          key: "donationCount",
                          label: "# of Donations",
                          style: { width: "130px" },
                          align: "center",
                        },
                      ]}
                      data={townTotals}
                      emptyMessage="No town-level totals are available."
                    />
                  </div>
                </div>

                <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                  <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                    Organization Totals
                  </div>
                  <div className="bg-white p-4">
                    <CommonTable
                      title=""
                      showAggregateTotal={false}
                      headerColor="bg-blue-50"
                      defaultSortField="amount"
                      defaultSortOrder="desc"
                      columns={[
                        {
                          key: "organizationName",
                          label: "Organization",
                          style: { width: "320px" },
                        },
                        {
                          key: "amount",
                          label: "Total Donated",
                          style: { width: "130px" },
                          align: "right",
                          render: (row) => formatMoney(row.amount),
                        },
                        {
                          key: "donationCount",
                          label: "# of Donations",
                          style: { width: "130px" },
                          align: "center",
                        },
                      ]}
                      data={organizationTotals}
                      emptyMessage="No organization totals are available."
                    />
                  </div>
                </div>
              </div>

              <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
                <div className="bg-gray-100 px-3 py-2 font-semibold text-lg">
                  Donation History
                </div>
                <div className="bg-white p-4">
                  <CommonTable
                    title=""
                    showAggregateTotal={false}
                    headerColor="bg-blue-50"
                    defaultSortField="transactionDateIso"
                    defaultSortOrder="desc"
                    columns={[
                      {
                        key: "contributorName",
                        label: "Name Used",
                        style: { width: "200px" },
                      },
                      {
                        key: "committeeName",
                        label: "Committee",
                        style: { width: "250px" },
                      },
                      {
                        key: "townName",
                        label: "Town",
                        style: { width: "120px" },
                      },
                      {
                        key: "transactionDate",
                        label: "Date",
                        style: { width: "110px" },
                        sortKey: "transactionDateIso",
                        render: (row) =>
                          formatDate(row.transactionDateIso || row.transactionDate),
                      },
                      {
                        key: "amount",
                        label: "Amount",
                        style: { width: "110px" },
                        align: "right",
                        render: (row) => formatMoney(row.amount),
                      },
                      {
                        key: "receiptType",
                        label: "Receipt Type",
                        style: { width: "260px" },
                      },
                      {
                        key: "methodOfPayment",
                        label: "Method",
                        style: { width: "140px" },
                      },
                      {
                        key: "occupation",
                        label: "Occupation",
                        style: { width: "160px" },
                      },
                      {
                        key: "employer",
                        label: "Employer",
                        style: { width: "180px" },
                      },
                    ]}
                    data={donations}
                    emptyMessage="No donation history is available for this donor."
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SeecDonorCard;
